import { ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/system';
import { createTheme } from '@mui/material/styles';
import { grey, red, amber, lightBlue } from '@mui/material/colors';
import { CssBaseline, GlobalStyles } from '@mui/material';

const ThemeProvider = ({ children }: { children: ReactNode }) => {
    const theme = createTheme({
        palette: {
            action: {
                disabledBackground: red[900],
                disabled: grey[50],
            },
            primary: {
                main: red[900],
                light: red[500],
            },
            secondary: {
                main: grey[50],
                light: grey[800],
            },
            background: {
                default: grey[900],
                paper: grey[900],
            },
            text: {
                primary: grey[50],
            },
        },
        typography: {
            fontFamily: 'Arial', // Default font
            fontSize: 14,
            fontWeightLight: 300,
            fontWeightRegular: 400,
            fontWeightMedium: 500,
            h4: {
                fontFamily: 'LemonMilk',
            },
            h5: {
                fontFamily: 'LemonMilk',
            },
        },
        components: {
            MuiCssBaseline: {
                styleOverrides: `
                  @font-face {
                    font-family: 'LemonMilk';
                    font-style: normal;
                    font-display: swap;
                    src: url('/fonts/LemonMilk.woff2') format('woff2'),url('/fonts/LemonMilk.woff') format('woff'),url('/fonts/LemonMilk.ttf') format('truetype');
                  }
                  * {
                    -webkit-tap-highlight-color: rgba(139, 0, 0, 0.15);
                  }
                  html {
                    scroll-behavior: smooth;
                  }
                  ::-webkit-scrollbar {
                    width: 6px;
                    height: 6px;
                  }
                  ::-webkit-scrollbar-track {
                    background: transparent;
                  }
                  ::-webkit-scrollbar-thumb {
                    background: rgba(139, 0, 0, 0.5);
                    border-radius: 3px;
                  }
                  ::-webkit-scrollbar-thumb:hover {
                    background: rgba(139, 0, 0, 0.8);
                  }
                `,
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        backgroundImage: 'none',
                    },
                },
            },
            MuiDialog: {
                styleOverrides: {
                    paper: {
                        borderRadius: '16px',
                        border: '1px solid rgba(139, 0, 0, 0.22)',
                        boxShadow: '0 24px 64px rgba(0,0,0,0.85), 0 0 0 1px rgba(139, 0, 0, 0.12)',
                        backgroundImage: 'none',
                    },
                },
            },
            MuiBackdrop: {
                styleOverrides: {
                    root: {
                        backdropFilter: 'blur(4px)',
                        backgroundColor: 'rgba(0, 0, 0, 0.65)',
                    },
                },
            },
            MuiDialogTitle: {
                styleOverrides: {
                    root: {
                        fontFamily: 'LemonMilk',
                        fontSize: '1.1rem',
                        paddingBottom: '12px',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.07)',
                    },
                },
            },
            MuiDialogActions: {
                styleOverrides: {
                    root: {
                        padding: '12px 20px 18px',
                        gap: '8px',
                    },
                },
            },
            MuiChip: {
                styleOverrides: {
                    root: {
                        transition: 'all 0.2s ease',
                        '&:hover': {
                            filter: 'brightness(1.15)',
                        },
                    },
                },
            },
            MuiIconButton: {
                styleOverrides: {
                    root: {
                        transition: 'color 0.2s ease, opacity 0.2s ease, transform 0.15s ease',
                        '&:active': {
                            transform: 'scale(0.88)',
                        },
                    },
                },
            },
            MuiFab: {
                styleOverrides: {
                    root: {
                        transition: 'box-shadow 0.2s ease',
                        '&:hover': {
                            boxShadow: '0 6px 20px rgba(139, 0, 0, 0.5)',
                        },
                    },
                },
            },
            MuiTextField: {
                styleOverrides: {
                    root: {
                        '& label': {
                            color: grey[50],
                        },
                        '&:hover label': {
                            color: red[900],
                        },
                        '& label.Mui-focused': {
                            color: grey[50],
                        },
                        '& .MuiInput-underline:after': {
                            borderBottomColor: grey[50],
                        },
                        '& .MuiOutlinedInput-root': {
                            color: grey[50],
                            '& fieldset': {
                                borderColor: grey[50],
                            },
                            '&:hover fieldset': {
                                borderColor: red[900],
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: red[900],
                                boxShadow: '0 0 0 3px rgba(139, 0, 0, 0.12)',
                            },
                        },
                        '& .MuiFormHelperText-root': {
                            color: grey[50],
                        },
                    },
                },
            },
            MuiSelect: {
                styleOverrides: {
                    root: {
                        color: grey[50],
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: grey[50],
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: red[900],
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: red[900],
                        },
                    },
                    icon: {
                        color: grey[50],
                    },
                },
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        color: grey[50],
                        '&.Mui-focused': {
                            color: grey[50],
                        },
                    },
                },
            },
            MuiTabs: {
                styleOverrides: {
                    root: {
                        '& .MuiTabs-indicator': {
                            height: '3px',
                            borderRadius: '3px 3px 0 0',
                            background: `linear-gradient(90deg, ${red[700]}, ${red[400]})`,
                            boxShadow: `0 0 8px rgba(139, 0, 0, 0.7)`,
                        },
                    },
                },
            },
            MuiTab: {
                styleOverrides: {
                    root: {
                        fontFamily: 'LemonMilk',
                        opacity: 0.55,
                        transition: 'opacity 0.2s ease, background-color 0.2s ease',
                        borderRadius: '8px 8px 0 0',
                        '&:hover': {
                            opacity: 0.85,
                            backgroundColor: 'rgba(139, 0, 0, 0.08)',
                        },
                        '&.Mui-selected': {
                            opacity: 1,
                            color: grey[50],
                        },
                    },
                },
            },
            MuiAlert: {
                styleOverrides: {
                    root: {
                        borderRadius: '12px',
                        backdropFilter: 'blur(8px)',
                        fontFamily: 'Arial',
                        fontSize: '0.9rem',
                    },
                    filledSuccess: {
                        background: 'linear-gradient(135deg, rgba(46,125,50,0.97), rgba(27,94,32,0.97))',
                        border: '1px solid rgba(76,175,80,0.35)',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                    },
                    filledError: {
                        background: 'linear-gradient(135deg, rgba(183,28,28,0.97), rgba(109,0,0,0.97))',
                        border: '1px solid rgba(211,47,47,0.35)',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                    },
                    filledWarning: {
                        background: 'linear-gradient(135deg, rgba(230,81,0,0.97), rgba(191,54,12,0.97))',
                        border: '1px solid rgba(255,152,0,0.35)',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                    },
                    filledInfo: {
                        background: 'linear-gradient(135deg, rgba(1,87,155,0.97), rgba(1,57,105,0.97))',
                        border: '1px solid rgba(33,150,243,0.35)',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                    },
                },
            },
            MuiSnackbar: {
                styleOverrides: {
                    root: {
                        '& .MuiSnackbarContent-root': {
                            borderRadius: '12px',
                            background: grey[800],
                            border: `1px solid rgba(139, 0, 0, 0.3)`,
                            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                            fontSize: '0.9rem',
                        },
                    },
                },
            },
            MuiButton: {
                styleOverrides: {
                    root: {
                        fontFamily: 'LemonMilk',
                        '&.Mui-disabled': {
                            opacity: 0.4,
                        },
                    },
                },
            },
            MuiMenuItem: {
                styleOverrides: {
                    root: {
                        fontFamily: 'LemonMilk',
                    },
                },
            },
            MuiToggleButton: {
                styleOverrides: {
                    root: {
                        fontFamily: 'LemonMilk',
                        color: grey[50],
                        opacity: 0.6,
                        '&.Mui-selected': {
                            color: red[900],
                            backgroundColor: `rgba(139, 0, 0, 0.12)`,
                            opacity: 1,
                            '&:hover': {
                                backgroundColor: `rgba(139, 0, 0, 0.2)`,
                            },
                        },
                        '&:hover': {
                            backgroundColor: `rgba(250, 250, 250, 0.08)`,
                            opacity: 0.8,
                        },
                    },
                },
            },
        },
    });

    return (
        <MuiThemeProvider theme={theme}>
            <CssBaseline enableColorScheme />
            <GlobalStyles
                styles={{
                    '.MuiDataGrid-menu .MuiPaper-root': {
                        '& .MuiMenuItem-root': {
                            color: grey[50],
                        },
                        '& .MuiListItemIcon-root': {
                            color: `${grey[50]} !important`,
                        },
                        '& .MuiSvgIcon-root': {
                            color: `${grey[50]} !important`,
                        },
                    },
                }}
            />
            {children}
        </MuiThemeProvider>
    );
};

export default ThemeProvider;

export const CommunityThemeProvider = ({ children }: { children: ReactNode }) => {
    return (
        <MuiThemeProvider
            theme={(outerTheme) =>
                createTheme(outerTheme, {
                    palette: {
                        action: {
                            disabledBackground: amber[900],
                            disabled: '#90a4ae',
                        },
                        primary: {
                            main: amber[600],
                            light: amber['A200'],
                        },
                        secondary: {
                            main: lightBlue[300],
                            light: lightBlue[900],
                        },
                        background: {
                            default: '#060f1e',
                            paper: '#0c1b30',
                        },
                        text: {
                            primary: '#e8f0fe',
                        },
                    },
                    components: {
                        MuiTabs: {
                            styleOverrides: {
                                root: {
                                    '& .MuiTabs-indicator': {
                                        height: '3px',
                                        borderRadius: '3px 3px 0 0',
                                        background: `linear-gradient(90deg, ${amber[500]}, ${lightBlue[400]})`,
                                        boxShadow: `0 0 10px ${amber[500]}99`,
                                    },
                                },
                            },
                        },
                        MuiTab: {
                            styleOverrides: {
                                root: {
                                    fontFamily: 'LemonMilk',
                                    opacity: 0.55,
                                    transition: 'opacity 0.2s ease, background-color 0.2s ease',
                                    borderRadius: '8px 8px 0 0',
                                    '&:hover': {
                                        opacity: 0.85,
                                        backgroundColor: 'rgba(255, 179, 0, 0.08)',
                                    },
                                    '&.Mui-selected': {
                                        opacity: 1,
                                        color: amber[300],
                                    },
                                },
                            },
                        },
                        MuiFab: {
                            styleOverrides: {
                                root: {
                                    transition: 'box-shadow 0.2s ease',
                                    '&:hover': {
                                        boxShadow: `0 6px 20px ${amber[600]}80`,
                                    },
                                },
                            },
                        },
                        MuiToggleButton: {
                            styleOverrides: {
                                root: {
                                    fontFamily: 'LemonMilk',
                                    color: '#e8f0fe',
                                    opacity: 0.6,
                                    '&.Mui-selected': {
                                        color: amber[400],
                                        backgroundColor: 'rgba(255, 179, 0, 0.12)',
                                        opacity: 1,
                                        '&:hover': {
                                            backgroundColor: 'rgba(255, 179, 0, 0.2)',
                                        },
                                    },
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 179, 0, 0.08)',
                                        opacity: 0.8,
                                    },
                                },
                            },
                        },
                        MuiDialog: {
                            styleOverrides: {
                                paper: {
                                    borderRadius: '16px',
                                    border: `1px solid ${amber[700]}38`,
                                    boxShadow: `0 24px 64px rgba(0,0,0,0.85), 0 0 0 1px ${amber[600]}1f`,
                                    backgroundImage: 'none',
                                },
                            },
                        },
                        MuiDialogTitle: {
                            styleOverrides: {
                                root: {
                                    fontFamily: 'LemonMilk',
                                    fontSize: '1.1rem',
                                    paddingBottom: '12px',
                                    borderBottom: '1px solid rgba(255, 255, 255, 0.07)',
                                },
                            },
                        },
                        MuiSnackbar: {
                            styleOverrides: {
                                root: {
                                    '& .MuiSnackbarContent-root': {
                                        borderRadius: '12px',
                                        background: '#0c1b30',
                                        border: `1px solid ${amber[700]}4d`,
                                        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                                        fontSize: '0.9rem',
                                    },
                                },
                            },
                        },
                        MuiChip: {
                            styleOverrides: {
                                root: {
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        filter: 'brightness(1.15)',
                                    },
                                },
                            },
                        },
                        MuiIconButton: {
                            styleOverrides: {
                                root: {
                                    transition: 'color 0.2s ease, opacity 0.2s ease, transform 0.15s ease',
                                    '&:active': {
                                        transform: 'scale(0.88)',
                                    },
                                },
                            },
                        },
                        MuiButton: {
                            styleOverrides: {
                                root: {
                                    fontFamily: 'LemonMilk',
                                    '&.Mui-disabled': {
                                        opacity: 0.4,
                                    },
                                },
                            },
                        },
                        MuiMenuItem: {
                            styleOverrides: {
                                root: {
                                    fontFamily: 'LemonMilk',
                                },
                            },
                        },
                    },
                })
            }
        >
            {children}
        </MuiThemeProvider>
    );
};
